import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SizeLimit } from '../../domain/regs-answer';

@Component({
  selector: 'app-size-ruler',
  standalone: true,
  template: `
    <div class="ruler-container">
      <div class="ruler-labels" #labelsContainer>
        @if (slot) {
          <div class="ruler-label ruler-label--single" [style.left.%]="midPct">
            {{ slotLabel }}
          </div>
        } @else {
          @if (minLimit) {
            <div class="ruler-label" [style.left.%]="minPct">
              {{ minLimit.number }} in min
            </div>
          }
          @if (maxLimit) {
            <div class="ruler-label" [style.left.%]="maxPct">
              {{ maxLimit.under ? 'under ' : '' }}{{ maxLimit.number }} in max
            </div>
          }
        }
        @if (minLimit) {
          <div class="ruler-tick" [style.left.%]="minPct"></div>
        }
        @if (maxLimit) {
          <div class="ruler-tick" [style.left.%]="maxPct"></div>
        }
      </div>
      <canvas #canvas class="ruler-canvas"></canvas>
    </div>
  `,
  styles: [
    `
      .ruler-container {
        position: relative;
        width: 100%;
        margin: 6px 0 12px;
        user-select: none;
      }
      .ruler-labels {
        position: relative;
        height: 28px;
        width: 100%;
      }
      .ruler-label {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        font-size: 14px;
        font-weight: 700;
        color: #137a43;
        white-space: nowrap;
      }
      .ruler-tick {
        position: absolute;
        top: 18px;
        width: 2px;
        height: 10px;
        background: #1f9d61;
        transform: translateX(-50%);
      }
      .ruler-canvas {
        display: block;
        width: 100%;
        height: 56px;
        border-radius: 6px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SizeRulerComponent implements AfterViewInit, OnChanges {
  @Input() min: SizeLimit | null = null;
  @Input() max: SizeLimit | null = null;
  @Input() askedInches: number | null = null;

  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  minLimit: SizeLimit | null = null;
  maxLimit: SizeLimit | null = null;
  slot = false;
  slotLabel = '';
  minPct = 0;
  maxPct = 100;
  midPct = 50;

  ngOnChanges(changes: SimpleChanges): void {
    this.calculateBounds();
    this.draw();
  }

  ngAfterViewInit(): void {
    this.calculateBounds();
    this.draw();
  }

  private calculateBounds(): void {
    this.minLimit = this.min;
    this.maxLimit = this.max;
    const values: number[] = [];
    if (this.minLimit) values.push(this.minLimit.inches);
    if (this.maxLimit) values.push(this.maxLimit.inches);
    if (values.length === 0) return;

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const lo = Math.max(0, Math.floor((minVal * 0.6) / 2) * 2);
    const hi = Math.ceil((maxVal * 1.4) / 2) * 2;
    const span = Math.max(1, hi - lo);

    if (this.minLimit) {
      this.minPct = Math.max(4, Math.min(96, ((this.minLimit.inches - lo) / span) * 100));
    }
    if (this.maxLimit) {
      this.maxPct = Math.max(4, Math.min(96, ((this.maxLimit.inches - lo) / span) * 100));
    }

    if (this.minLimit && this.maxLimit) {
      this.midPct = (this.minPct + this.maxPct) / 2;
      this.slot = Math.abs(this.maxPct - this.minPct) < 26;
      this.slotLabel = `${this.minLimit.number} to ${this.maxLimit.under ? 'under ' : ''}${this.maxLimit.number} in`;
    } else {
      this.slot = false;
    }
  }

  private draw(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = rect.width || 340;
    const h = 56;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const values: number[] = [];
    if (this.minLimit) values.push(this.minLimit.inches);
    if (this.maxLimit) values.push(this.maxLimit.inches);
    if (values.length === 0) return;

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const lo = Math.max(0, Math.floor((minVal * 0.6) / 2) * 2);
    const hi = Math.ceil((maxVal * 1.4) / 2) * 2;
    const span = Math.max(1, hi - lo);

    const x = (v: number) => ((v - lo) / span) * w;

    const steps = [1, 2, 4, 5, 10, 20, 25, 50];
    const step = steps.find((s) => span / s <= 7) || 50;

    const keepFrom = this.minLimit?.inches ?? lo;
    const keepTo = this.maxLimit?.inches ?? hi;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    // Red under zone
    if (keepFrom > lo) {
      ctx.fillStyle = 'rgba(195, 40, 58, 0.12)';
      ctx.fillRect(0, 0, x(keepFrom), h);
    }

    // Green keep zone
    ctx.fillStyle = 'rgba(31, 157, 97, 0.22)';
    ctx.fillRect(x(keepFrom), 0, Math.max(0, x(keepTo) - x(keepFrom)), h);

    // Red over zone
    if (keepTo < hi) {
      ctx.fillStyle = 'rgba(195, 40, 58, 0.12)';
      ctx.fillRect(x(keepTo), 0, Math.max(0, w - x(keepTo)), h);
    }

    // Ticks & Numbers
    ctx.strokeStyle = '#3a3a3c';
    ctx.fillStyle = '#3a3a3c';
    ctx.lineWidth = 1.5;
    ctx.font = '600 12px -apple-system, sans-serif';
    ctx.textAlign = 'center';

    for (let v = lo; v <= hi + 0.001; v += 1) {
      const pos = x(v);
      const isMajor = Math.abs(v % step) < 0.001;
      const tickH = isMajor ? 22 : v % 2 === 0 ? 14 : 8;

      ctx.beginPath();
      ctx.moveTo(pos, 0);
      ctx.lineTo(pos, tickH);
      ctx.stroke();

      if (isMajor && v > lo && v < hi) {
        if (pos >= 18 && pos <= w - 14) {
          ctx.fillText(v.toFixed(0), pos, h - 8);
        }
      }
    }

    // Outer border
    ctx.strokeStyle = '#3a3a3c';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(0, 0, w, h);

    // Bump stop at left
    ctx.fillStyle = '#3a3a3c';
    ctx.fillRect(0, 0, 8, h);
  }
}
